import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { RootStoreContext } from '../../stores/rootStore';
import { observer } from "mobx-react-lite";
import { saveVisit } from '../../api/visitTrackerService';
import spinner from '../../assets/images/Spinner-1s-55px.gif';
import Maintenance from './maintenance';
import { PAYMENTS_DISABLED } from '../../constants';

const Purchase = observer(() => {
    const { quantity } = useParams();
    const rootStore = useContext(RootStoreContext);
    const purchaseStore = rootStore.purchaseStore;
    const userStore = rootStore.userStore;
    const { loading } = rootStore;
    const bundle = purchaseStore.bundle;
    const { Qnt, increaseQnt, decreaseQnt } = bundle;

    const {
        iHaveDiscount, isDiscountValid, discountError,
        discountCode, discountPercentageString, codeDiscountString,
        totalDiscountString, showCheckingDiscountSpinner, totalAfterDiscountString,
        USDPrice, EURPrice, USDPriceString, EURPriceString,
        isCheckoutClicked, isDiscountQntMatched, discountMinimumQntString,
        isInvoiceNeeded, invoiceBusinessName, invoiceAddress,
        invoiceCountryIsoCode, invoiceRefNumber, countries,
    } = purchaseStore;

    const { isLoggedIn, OpenLoginForm, OpenRegisterForm } = userStore;
    const curr = rootStore.currency === 'EUR' ? '€' : '£';

    //billing period: the pricing page hands the cycle over as ?cycle=monthly|annual
    const isMonthly = purchaseStore.isMonthly;
    const annual1 = rootStore.priceList[1];
    const monthly1 = rootStore.monthlyPriceList[1];
    const savePct = annual1 > 0 && monthly1 > 0
        ? Math.round((1 - annual1 / (monthly1 * 12)) * 100)
        : 0;

    useEffect(() => {
        const qty = quantity ? Number(quantity) : (Qnt || 1);
        bundle.setQnt(qty);
        const cycle = new URLSearchParams(window.location.search).get('cycle');
        if (cycle === 'monthly' || cycle === 'annual') {
            purchaseStore.setBillingPeriod(cycle);
        }
        saveVisit('purchase');
    }, []);

    const handleQntInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        bundle.setQnt(Number(e.target.value));
    };
    const handlePromoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        purchaseStore.setDiscountCode(e.target.value);
    };
    const handleIncreaseQnt = () => {
        purchaseStore.increaseQntToMatchDiscountMinimumQnt();
    };

    if (PAYMENTS_DISABLED || purchaseStore.paymentProvider === 'maintenance') return <Maintenance />;
    if (loading || !purchaseStore.paymentProvider) return <div className="page-center"><p>Loading…</p></div>;

    return (
        <div className="order-wrap">
            <h1 className="page-title">Order summary</h1>

            {purchaseStore.monthlyAvailable && (
                <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                    <div className="billing-switch" style={{ margin: 0 }} role="tablist" aria-label="Billing period">
                        <button
                            type="button"
                            className={`bs-opt${isMonthly ? ' active' : ''}`}
                            role="tab"
                            aria-selected={isMonthly}
                            onClick={() => purchaseStore.setBillingPeriod('monthly')}
                        >Monthly</button>
                        <button
                            type="button"
                            className={`bs-opt${!isMonthly ? ' active' : ''}`}
                            role="tab"
                            aria-selected={!isMonthly}
                            onClick={() => purchaseStore.setBillingPeriod('annual')}
                        >Yearly{savePct > 0 && <span className="bs-save">Save {savePct}%</span>}</button>
                    </div>
                </div>
            )}

            <div className="order-card">
                <div className="order-head">
                    <img src="/logo.svg" alt="" />
                    <div>
                        <div className="order-head-title">Desktop Call Recorder Pro</div>
                        <div className="order-head-sub">{isMonthly ? 'Monthly' : 'Annual'} subscription · billed in {rootStore.currency}</div>
                    </div>
                </div>

                <div className="order-body">
                    <div className="row">
                        <span className="row-label">
                            PRO subscription
                            <small>× {isMonthly ? '1 month' : '12 months'}</small>
                        </span>
                        <span className="row-val muted">{isMonthly ? 'monthly' : 'annual'}</span>
                    </div>

                    <div className="row">
                        <span className="row-label">Number of computers</span>
                        <div className="order-qty">
                            <button type="button" onClick={decreaseQnt}>−</button>
                            <input type="number" value={Qnt} onChange={handleQntInputChange} name="custom-input-number" />
                            <button type="button" onClick={increaseQnt}>+</button>
                        </div>
                    </div>

                    {bundle.Saving > 0 && (
                        <div className="row">
                            <span className="row-label">Volume discount</span>
                            <span className="row-val discount">{curr}{bundle.SavingString}</span>
                        </div>
                    )}

                    <div className="row">
                        <span className="row-label">
                            Per computer per {isMonthly ? 'month' : 'year'}
                            {!isMonthly && <small>{curr}{bundle.PricePerMonthString} × 12</small>}
                        </span>
                        <span className="row-val">{curr}{bundle.PricePerComputerString}</span>
                    </div>

                    {isMonthly ? null : iHaveDiscount ? (
                        isDiscountValid ? (
                            isDiscountQntMatched ? (
                                <>
                                    <div className="row">
                                        <span className="row-label">Code <strong>{discountCode}</strong> ({discountPercentageString}%)</span>
                                        <span className="row-val discount">{curr}{codeDiscountString}</span>
                                    </div>
                                    <div className="row">
                                        <span className="row-label">Total savings</span>
                                        <span className="row-val discount">{curr}{totalDiscountString}</span>
                                    </div>
                                </>
                            ) : (
                                <div className="row">
                                    <span className="row-label">
                                        Code <strong>{discountCode}</strong> requires at least <strong>{discountMinimumQntString}</strong> subscriptions.{' '}
                                        <button className="btn-link" onClick={handleIncreaseQnt}>Increase quantity</button>
                                    </span>
                                </div>
                            )
                        ) : (
                            <div className="row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                                <span className="row-label">Promo code</span>
                                {showCheckingDiscountSpinner ? (
                                    <span style={{ fontSize: '.84rem', color: 'var(--muted)' }}>Checking…</span>
                                ) : (
                                    <div className="discount-input">
                                        <input type="text" value={discountCode} onChange={handlePromoInputChange} placeholder="Enter code" />
                                    </div>
                                )}
                                {discountError && <span className="dcr-error">{discountError}</span>}
                            </div>
                        )
                    ) : (
                        <div className="row">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '.84rem', color: 'var(--text2)', cursor: 'pointer', fontWeight: 500 }}>
                                <input type="checkbox" checked={iHaveDiscount} onChange={e => purchaseStore.setIHaveDiscount(e.target.checked)} />
                                I have a discount code
                            </label>
                        </div>
                    )}
                </div>

                <div className="total-row">
                    <span className="row-label">{isMonthly ? 'Total per month' : 'Total'}</span>
                    <span className="row-val">{curr}{totalAfterDiscountString}</span>
                </div>

                {rootStore.currency !== 'EUR' && USDPrice !== 0 && EURPrice !== 0 && (
                    <p className="conv-line">≈ USD {USDPriceString} or EUR {EURPriceString}</p>
                )}

                {purchaseStore.subscriptionMode ? (
                    isMonthly ? (
                        <p className="checkout-note">
                            You’ll be redirected to our secure payment provider to complete your purchase.
                            This is a <strong>monthly subscription that renews automatically</strong> each
                            month at the then-current price — cancel anytime and Pro stays active until the
                            end of the paid period. Tax is handled at checkout and your invoice is available
                            on your subscriptions page right after payment.
                        </p>
                    ) : (
                        <p className="checkout-note">
                            You’ll be redirected to our secure payment provider to complete your purchase.
                            This is an <strong>annual subscription that renews automatically</strong> each
                            year at the then-current price — you can cancel anytime. Tax is handled at
                            checkout and your invoice is available on your subscriptions page right after payment.
                        </p>
                    )
                ) : purchaseStore.managedPayments ? (
                    <p className="checkout-note">
                        You’ll be redirected to our secure payment provider to complete your purchase.
                        Tax is handled at checkout and your invoice will be available on your
                        subscriptions page right after payment.
                    </p>
                ) : (
                    <p className="checkout-note">
                        You’ll be redirected to our secure payment provider to complete your purchase.
                    </p>
                )}

                {purchaseStore.paymentProvider === 'stripe' && !purchaseStore.managedPayments && (
                    <div className="invoice-section">
                        <label className="invoice-toggle">
                            <input type="checkbox" checked={isInvoiceNeeded} onChange={e => purchaseStore.setInvoiceNeeded(e.target.checked)} />
                            I need an invoice
                        </label>

                        {isInvoiceNeeded && (
                            <div className="invoice-fields">
                                <div className="full">
                                    <label>Name / Business name</label>
                                    <input className={purchaseStore.invoiceBusinessNameFieldStyle} name="Name" type="text" value={invoiceBusinessName} onChange={e => purchaseStore.setInvoiceBusinessName(e.target.value)} />
                                </div>
                                <div className="full">
                                    <label>Billing address</label>
                                    <textarea className={purchaseStore.invoiceAddressFieldStyle} name="Address" rows={3} value={invoiceAddress} onChange={e => purchaseStore.setInvoiceAddress(e.target.value)} />
                                </div>
                                <div>
                                    <label>Country (from your location)</label>
                                    <input type="text" value={countries?.find(c => c.code === invoiceCountryIsoCode)?.name ?? invoiceCountryIsoCode} readOnly disabled />
                                </div>
                                <div>
                                    <label>Your reference (optional)</label>
                                    <input type="text" name="BuyersRefNumber" value={invoiceRefNumber} onChange={e => purchaseStore.setInvoiceRefNumber(e.target.value)} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isLoggedIn ? (
                    <div className="checkout-actions">
                        {purchaseStore.PurchaseError && (
                            <span className="dcr-error">{purchaseStore.PurchaseError}</span>
                        )}
                        {isCheckoutClicked ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--muted)', fontSize: '.9rem' }}>
                                <img src={spinner} alt="" style={{ height: '28px' }} />
                                Opening secure checkout…
                            </span>
                        ) : (
                            <button type="button" className="btn btn-primary" onClick={() => purchaseStore.HandleCheckoutClicked()}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                                </svg>
                                Checkout
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="auth-section">
                        <h3>Sign in to complete your order</h3>
                        <div className="auth-actions">
                            <button type="button" className="btn btn-primary" onClick={OpenLoginForm}>Sign in</button>
                            <button type="button" className="btn btn-outline" onClick={OpenRegisterForm}>New customer</button>
                        </div>
                        <div className="secure-note">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="14" height="14">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                            </svg>
                            Payment processed securely
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
});

export default Purchase;
